import { Input } from '@thanx/uikit/input'
import { Switch } from '@thanx/uikit/switch'
import { useStyletron } from '@thanx/uikit/theme'
import { buildTranslate } from 'locales'
import React from 'react'
import { merchantTheme } from 'theme'

type Props = {
  value: string
  onChange: (value: string) => void
  className?: string
  placeholder?: string
  // Override the default "Search" accessible name so consumers (e.g. the shared
  // TableToolbar) can word it per surface. Falls back to the generic label.
  ariaLabel?: string
  // View-only RBAC surfaces (e.g. RecoveryAI RestrictedCustomers for a
  // can-view-not-edit operator) disable the whole search affordance, including
  // the clear button, rather than just withholding the mutating controls.
  disabled?: boolean
}

const t = buildTranslate('components.search_input')

const SearchInput: React.FC<Props> = ({
  value,
  onChange,
  className = '',
  placeholder,
  ariaLabel,
  disabled = false,
}) => {
  const [css] = useStyletron()

  return (
    <div className={`relative ${className}`}>
      <i
        className={`fa fa-search absolute ${css({
          top: '16px',
          left: '16px',
          color: merchantTheme.colors.grey90,
        })}`}
        aria-hidden="true"
      />
      <Input
        onChange={onChange}
        value={value}
        disabled={disabled}
        aria-label={ariaLabel ?? t('label')}
        overrides={{
          Input: {
            style: () => ({
              paddingLeft: '48px',
              paddingRight: '48px',
            }),
          },
        }}
        placeholder={placeholder}
      />
      <Switch condition={value !== '' && !disabled}>
        <button
          tabIndex={0}
          type="button"
          className={`position-absolute close ${css({
            top: '12px',
            right: '16px',
            opacity: 1,
            color: merchantTheme.colors.grey90,
            ':focus': {
              background: merchantTheme.colors.grey10,
              border: `1px solid ${merchantTheme.colors.grey90}`,
              borderRadius: '4px',
              padding: '0px 4px',
            },
            ':not(:focus)': {
              padding: '0px 4px',
            },
          })}`}
          aria-label={t('clear')}
          onClick={() => onChange('')}
        >
          &times;
        </button>
      </Switch>
    </div>
  )
}

export default SearchInput
